import Vue from 'vue'
import { keys, sortBy, reverse } from 'lodash'
import uuid from 'uuid/v4'
import { auth, sheets, drive } from '../services/google'

const GoogleModule = {
  state: {
    loginStatus: "",
    trackedRequests: {},
    completedRequests: {}
  },

  getters: {
    showSpinner: state => keys(state.trackedRequests).length > 0,
    loginStatus: state => state.loginStatus,
    trackedRequests: state => reverse(sortBy(state.trackedRequests, "timestamp")),
    completedRequests: state => reverse(sortBy(state.completedRequests, "timestamp"))
  },

  mutations: {
    addTrackedRequest(state, { id, name, details="" }) {
      let request = { id, name, details, timestamp: Date.now() }
      Vue.set(state.trackedRequests, id, request)
    },

    errorTrackedRequest(state, { requestId, requestError }) {
      const loc = (requestError.fileName && requestError.lineNumber)
        ? `${requestError.fileName}:${requestError.lineNumber} — `
        : ''
      state.trackedRequests[requestId].error = `${loc}${requestError.message || String(requestError)}`
    },

    clearTrackedRequest(state, requestId) {
      Vue.set(state.completedRequests, requestId, state.trackedRequests[requestId])
      Vue.delete(state.trackedRequests, requestId)
    },

    setLoginStatus(state, newStatusMessage) {
      state.loginStatus = `${newStatusMessage}\n`
    },

    appendLoginStatus(state, nextStatusMessage) {
      state.loginStatus += `${nextStatusMessage}\n`
    }
  },

  actions: {
    googleLogin() {
      return auth.signIn()
    },

    googleLogout() {
      return auth.signOut()
    },

    googleRevokeAccess() {
      return auth.revokeAccess()
    },

    async traceNetworkRequest({ commit }, { name, details, promise }) {
      const requestId = uuid()
      commit("addTrackedRequest", { id: requestId, name, details })
      try {
        return await Promise.resolve(promise)
      } catch (requestError) {
        const is401 = requestError.status === 401
          || requestError.result?.error?.code === 401
          || String(requestError.body || requestError.message || '').includes('"UNAUTHENTICATED"')

        if (is401) {
          let refreshed = false
          try {
            await auth.refreshToken()
            refreshed = true
          } catch (_) {
            await auth.signOut()
          }
          const msg = refreshed
            ? 'Google session expired and was refreshed — please retry your last action.'
            : 'Google session expired — please sign out and sign in again.'
          const friendlyError = new Error(msg)
          commit("errorTrackedRequest", { requestId, requestError: friendlyError })
          throw friendlyError
        }

        commit("errorTrackedRequest", { requestId, requestError })
        throw requestError
      } finally {
        commit("clearTrackedRequest", requestId)
      }
    },

    googleBatchRequests({ dispatch }, requests) {
      return dispatch("traceNetworkRequest",
        { name: "Get Batch",
          details: `Batching ${requests.length} requests`,
          promise: drive.doBatchRequest(requests) })
    },

    googleGetRecord({ dispatch }, fileId) {
      return dispatch("traceNetworkRequest",
        { name: "Get Record.",
          details: fileId,
          promise: drive.getRecord(fileId) })
    },

    googleBatchGetTrackedFileIndex({ dispatch }, folderIds) {
      return dispatch("traceNetworkRequest",
        { name: `Index Folders.`,
          details: `Indexing ${folderIds.length} Folders.`,
          promise: drive.getIndex(folderIds).then(index => {
            return dispatch("touchFolders", folderIds)
              .then(() => index)
          })
        })
    },

    googleCreateFolder({ dispatch }, { parentId, name }) {
      return dispatch("traceNetworkRequest",
        { name: `Create Folder.`,
          details: `Named: "${name}" in folder ${parentId}`,
          promise: drive.createFolder(name, { parentId }) })
    },

    googleCreateSpreadsheet({ dispatch }, { parentId, name }) {
      return dispatch("traceNetworkRequest",
        { name: `Create Spreadsheet.`,
          details: `Named: "${name}" in folder ${parentId}`,
          promise: drive.createSpreadsheet(name, parentId)
        })

        .then(spreadsheetId => {
          // Add the Sheet
          return dispatch("createSpreadsheet", {
            id: spreadsheetId,
            name: name,
            parents: [parentId]
          }).then(() => {
            // Index the Sheet
            return dispatch("refreshSheetIndex", spreadsheetId)
          }).then(() => spreadsheetId)
        })
    },

    googleAddSheetToSpreadsheet({ dispatch }, { spreadsheetId, sheetName }) {
      return dispatch("traceNetworkRequest",
        { name: `Add Sheet to Spreadsheet.`,
          details: `Named: "${sheetName}"`,
          promise: sheets.addSheetToSpreadsheet(spreadsheetId, sheetName)
        })

        .then(trace => {
          // Refresh our local version of this sheet
          return dispatch("refreshSheetIndex", spreadsheetId)
            .then(() => trace)
        })
    },

    googleUpdateFile({ dispatch }, { fileId, contents }) {
      return dispatch("traceNetworkRequest",
        { name: `Update File.`,
          details: fileId,
          promise: drive.updateFile(fileId, contents) })
    },

    googleDownloadFile({ dispatch }, fileId) {
      return dispatch("traceNetworkRequest",
        { name: `Download File.`,
          details: fileId,
          promise: drive.downloadFile(fileId) })
    },

    googleFetchSheetById({ dispatch }, sourceId) {
      return dispatch("traceNetworkRequest",
        { name: `Fetch Sheet.`,
          details: sourceId,
          promise: sheets.fetchSheetById(sourceId) })
    },

    googleUpdateSheetRow({ dispatch }, { spreadsheetId, worksheetTitle, rowNumber, values }) {
      return dispatch("traceNetworkRequest",
        { name: `Update Sheet Row.`,
          details: `Row ${rowNumber} in ${spreadsheetId}`,
          promise: sheets.updateSheetRow(spreadsheetId, worksheetTitle, rowNumber, values) })
    },

    googleAppendSheetRow({ dispatch }, { spreadsheetId, worksheetTitle, values }) {
      return dispatch("traceNetworkRequest",
        { name: `Append Sheet Row.`,
          details: `Append to ${spreadsheetId}`,
          promise: sheets.appendSheetRow(spreadsheetId, worksheetTitle, values) })
    },

    googleInsertSheetRow({ dispatch }, { spreadsheetId, sheetId, rowIndex }) {
      return dispatch("traceNetworkRequest",
        { name: `Insert Sheet Row.`,
          details: `Row ${rowIndex} in ${spreadsheetId}`,
          promise: sheets.insertSheetRow(spreadsheetId, sheetId, rowIndex) })
    },

    googleDeleteSheetRow({ dispatch }, { spreadsheetId, sheetId, rowIndex }) {
      return dispatch("traceNetworkRequest",
        { name: `Delete Sheet Row.`,
          details: `Row ${rowIndex} in ${spreadsheetId}`,
          promise: sheets.deleteSheetRow(spreadsheetId, sheetId, rowIndex) })
    },
  }
}

export default GoogleModule
